import Button from "@atoms/button";
import { useWindowSize } from "@hooks/window-size";
import { FC, useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import styled from "styled-components";
import { useFHConfig } from "@templates/config";

export type GalleryPreviewPhoto = {
  title: string;
  resource: string;
};

export type GalleryPreviewProps = {
  onPhotoClick?: (position?: number) => void;
  onClikShowAll?: () => void;
  photos: GalleryPreviewPhoto[];
};

export const GalleryPreview: FC<GalleryPreviewProps> = ({
  onPhotoClick,
  onClikShowAll,
  photos,
}) => {
  const config = useFHConfig();
  const { width } = useWindowSize();
  const mobileVersion = useMemo(() => width && width < 768, [width]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const photosToRender = useMemo(
    () => (mobileVersion ? photos : photos.slice(0, 5)),
    [mobileVersion, photos]
  );

  const [loadedPhotos, setLoadedPhotos] = useState<number[]>([]);

  useEffect(() => {
    if (mobileVersion) {
      const checkLoaded = (index: number) => {
        setLoadedPhotos((current) => {
          if (current.indexOf(index) >= 0) {
            return current;
          } else {
            return [...current, index];
          }
        });
      };
      if (currentSlide > 0) checkLoaded(currentSlide - 1);
      else checkLoaded(photos.length - 1);
      checkLoaded(currentSlide);
      if (photos.length - 1 >= currentSlide) checkLoaded(0);
      checkLoaded(currentSlide + 1);
    } else {
      setLoadedPhotos([0, 1, 2, 3, 4, 5]);
    }
  }, [currentSlide, photos, mobileVersion]);

  const photosComponent = useMemo(
    () =>
      photosToRender.map((photo, index) => (
        <div
          key={`photo-${index}`}
          className="photo"
          onClick={() => onPhotoClick && onPhotoClick(index)}
        >
          {loadedPhotos.indexOf(index) >= 0 ? (
            <picture>
              <source
                srcSet={
                  index === 0
                    ? `${config.backUrl}/assets/${photo.resource}?fit=cover&width=1800&height=1800`
                    : `${config.backUrl}/assets/${photo.resource}?fit=cover&width=900&height=900`
                }
                media="(min-width: 75em)"
              />
              <source
                srcSet={
                  index === 0
                    ? `${config.backUrl}/assets/${photo.resource}?fit=cover&width=1200&height=1200`
                    : `${config.backUrl}/assets/${photo.resource}?fit=cover&width=600&height=600`
                }
                media="(min-width: 48em)"
              />
              <source
                srcSet={`${config.backUrl}/assets/${photo.resource}?fit=cover&width=600&height=600&format=webp`}
                type="image/webp"
              />
              <img
                alt={photo.title}
                src={`${config.backUrl}/assets/${photo.resource}?fit=cover&width=600&height=600`}
                loading="lazy"
              />
            </picture>
          ) : (
            <span />
          )}
          <div className="overlay" />
        </div>
      )),
    [onPhotoClick, photosToRender, loadedPhotos, config.backUrl]
  );

  return (
    <Container>
      {mobileVersion ? (
        <div>
          <Slider afterChange={setCurrentSlide}>{photosComponent}</Slider>
          <span className="photos-counter">
            {currentSlide + 1} / {photosComponent.length}
          </span>
        </div>
      ) : (
        <div>
          <div className="gallery-photos">{photosComponent}</div>
          <Button onClick={onClikShowAll} variant="primary">
            Show all photos
          </Button>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  overflow: hidden;

  .slick-slider {
    .slick-arrow {
      z-index: 100;
      &.slick-prev {
        left: 0.25rem;
      }
      &.slick-next {
        right: 0.25rem;
      }
    }
  }

  .photo {
    cursor: pointer;

    img {
      display: block;
      width: 100%;
      height: auto;
    }

    .overlay {
      pointer-events: none;
      position: absolute;
      z-index: 101;
      width: 100%;
      height: 100%;
      background-color: #000;
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }

    :hover,
    :focus {
      .overlay {
        opacity: 0.1;
      }
    }
  }

  button {
    display: none;
    position: absolute;
    right: 2em;
    bottom: 2em;
  }

  .photos-counter {
    position: absolute;
    right: 1em;
    bottom: 1em;
    font-family: "Work Sans", Arial;
    font-size: 0.8em;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.8);
    padding: 0.5em 1em;
    border-radius: 0.5em;
  }

  @media (min-width: 48em) {
    border-radius: 1em;
    margin: 0 auto;

    button {
      display: block;
    }

    .photos-counter {
      display: none;
    }

    .gallery-photos {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: repeat(2, 15em);
      gap: 0.25rem;

      .photo {
        overflow: hidden;
        position: relative;
        background-position: 50% 50%;
        background-repeat: no-repeat;

        img {
          object-fit: cover;
          position: absolute;
          width: 100%;
          height: 100%;
        }

        &:first-child {
          grid-row: span 2;
          grid-column: span 2;
        }
      }
    }
  }
`;
